import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StatusBadge } from "@/pages/Dashboard";
import { supportCode, matchesSupportCode } from "@/lib/supportCode";
import SupportCodeBadge from "@/components/SupportCodeBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import SdiErrorDialog from "@/components/SdiErrorDialog";
import {
  FileText, Search, Upload, Loader2, ArrowRight, ArrowUpRight,
  Filter, Calendar, Coins, Sparkles, ChevronLeft, ChevronRight,
  Send, ClipboardCheck, X, RefreshCw,
} from "lucide-react";

const PAGE_SIZE = 15;

const STATUS_OPTIONS = [
  { value: "all", label: "Tutti gli stati" },
  { group: "Flusso interno" },
  { value: "uploaded", label: "Caricata" },
  { value: "extracting", label: "In estrazione" },
  { value: "extracted", label: "Estratta" },
  { value: "reviewed", label: "Verificata" },
  { value: "xml_generated", label: "XML pronto" },
  { value: "error", label: "Errore" },
  { group: "Stato SDI" },
  { value: "sdi:not_sent", label: "Non trasmessa" },
  { value: "sdi:pending", label: "In attesa esito SDI" },
  { value: "sdi:RC", label: "SDI · Consegnata (RC)" },
  { value: "sdi:MC", label: "SDI · Mancata consegna (MC)" },
  { value: "sdi:NS", label: "SDI · Scartata (NS)" },
  { value: "sdi:EC", label: "SDI · Esito committente (EC)" },
  { value: "sdi:NE", label: "SDI · Notif. esito (NE)" },
  { value: "sdi:AT", label: "SDI · Attestazione (AT)" },
  { value: "sdi:DT", label: "SDI · Termini decorsi (DT)" },
  { value: "sdi:manual", label: "Trasmessa manualmente" },
];

// Mirrors backend openapi_sdi.SDI_STATUS_LABELS (kept in sync with Dashboard).
const SDI_LABELS = {
  RC: { label: "Consegnata", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  MC: { label: "Mancata consegna", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  NS: { label: "Scartata", cls: "bg-red-50 text-red-700 border-red-200" },
  DT: { label: "Termini decorsi", cls: "bg-[#F5F2EA] text-[#4A4A4A] border-[#E8E4D9]" },
  NE: { label: "Esito committente", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  EC: { label: "Esito committente", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  AT: { label: "Attestazione", cls: "bg-blue-50 text-blue-700 border-blue-200" },
};

function SdiTxBadge({ inv }) {
  const code = (inv.sdi_status || "").toUpperCase();
  const sent = inv.lock_state?.sdi_sent || inv.lock_state?.fic_sent;
  const manual = inv.lock_state?.manual_transmission;
  if (manual) {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider bg-[#F5F2EA] text-[#0A0A0A] border-[#0A0A0A]/40"
        data-testid="sdi-tx-manual"
      >
        Trasmessa manualmente
      </span>
    );
  }
  if (!sent) {
    return <span className="text-[10px] text-[#A3A3A3] italic">Non trasmessa</span>;
  }
  if (code && SDI_LABELS[code]) {
    const m = SDI_LABELS[code];
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider ${m.cls}`}
        data-testid={`sdi-tx-${code}`}
      >
        {m.label}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wider bg-blue-50 text-blue-700 border-blue-200"
      data-testid="sdi-tx-pending"
    >
      In transito
    </span>
  );
}

export default function InvoiceList() {
  // Deep-linking via URL query params (v17g): il Dashboard passa
  // `?status=sdi:RC` (e simili) cliccando un contatore. Leggiamo il param
  // al mount e lo teniamo sincronizzato con lo statusFilter così un F5
  // preserva il filtro e lo storico browser funziona correttamente.
  const [searchParams, setSearchParams] = useSearchParams();
  // ---- Server-driven state (Feb 29, 2026 v12) ----
  // Filter/paginate/sort work server-side so the browser never holds more
  // than a single page's worth of docs. Scales to millions of invoices per
  // tenant without any client-side memory pressure.
  const [invoices, setInvoices] = useState([]);        // items del server per la pagina corrente
  const [total, setTotal] = useState(0);               // count del filtered set (server)
  const [pages, setPages] = useState(1);
  const [stats, setStats] = useState(null);            // KPI card values (aggregation Mongo)
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    () => searchParams.get("status") || "all",
  );
  const [page, setPage] = useState(1);                 // 1-indexed (v12)
  // Bulk selection: set of invoice ids the user has ticked
  const [selected, setSelected] = useState(() => new Set());
  const [showBulkSdi, setShowBulkSdi] = useState(false);
  const [showBulkManual, setShowBulkManual] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sdiError, setSdiError] = useState(null);

  // Debounce the search box so we don't fire a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Sync URL ↔ statusFilter: quando l'utente cambia il filtro dal dropdown,
  // aggiorniamo l'URL così il browser back/forward + F5 preservano il filtro.
  useEffect(() => {
    const current = searchParams.get("status") || "all";
    if (current !== statusFilter) {
      if (statusFilter === "all") {
        // Clean up: remove the param entirely instead of ?status=all
        searchParams.delete("status");
        setSearchParams(searchParams, { replace: true });
      } else {
        searchParams.set("status", statusFilter);
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [statusFilter]);

  // Sync statusFilter ← URL: quando l'utente naviga via back/forward o
  // arriva da un link con `?status=sdi:RC`, aggiorniamo lo state locale.
  useEffect(() => {
    const urlStatus = searchParams.get("status") || "all";
    if (urlStatus !== statusFilter) {
      setStatusFilter(urlStatus);
    }
  }, [searchParams]);

  const load = async ({ withSdiRefresh = false } = {}) => {
    try {
      const params = { page, limit: PAGE_SIZE };
      if (debouncedQuery) params.q = debouncedQuery;
      if (statusFilter !== "all") params.status = statusFilter;
      const [invRes, statsRes] = await Promise.all([
        api.get("/invoices", { params }),
        api.get("/invoices/stats"),
      ]);
      // Server risponde `{items, total, page, limit, pages}`. Manteniamo
      // fallback per compat con vecchie versioni della risposta.
      const payload = invRes.data;
      const items = Array.isArray(payload) ? payload : (payload?.items || []);
      setInvoices(items);
      setTotal(payload?.total ?? items.length);
      setPages(payload?.pages ?? 1);
      setStats(statsRes.data);
    } finally {
      setLoading(false);
    }
    if (withSdiRefresh) {
      api.post("/invoices/refresh-all-sdi")
        .then(({ data }) => { if (data?.updates > 0) load(); })
        .catch(() => {});
    }
  };

  useEffect(() => {
    load({ withSdiRefresh: true });
  }, []);

  // Refetch quando l'utente cambia filtro / query / pagina. Il reset a
  // page=1 è gestito dall'effect qui sotto — questo altrimenti ci farebbe
  // fetchare "page=2 su un set filtrato di 8" e restare vuoti.
  useEffect(() => {
    if (loading) return; // primo mount lo gestisce l'effect precedente
    load();
  }, [debouncedQuery, statusFilter, page]);

  // Reset a page 1 quando query o status filter cambiano — evita che
  // l'utente resti su "page 3" dopo aver stretto i filtri.
  useEffect(() => { setPage(1); }, [debouncedQuery, statusFilter]);

  // KPI values derivati da /invoices/stats (aggregation Mongo, non lo
  // stato locale). Al primo render mostriamo 0 mentre lo stats arriva.
  const kpi = useMemo(() => {
    const byStatus = stats?.by_status || {};
    const estratte = (byStatus.extracted || 0) + (byStatus.reviewed || 0) + (byStatus.xml_generated || 0);
    return {
      total: stats?.total ?? 0,
      estratte,
      xmlReady: byStatus.xml_generated || 0,
      volumeEur: stats?.total_amount_eur || 0,
    };
  }, [stats]);

  // ----- Bulk selection helpers -----
  // An invoice is "bulk-actionable" if it has an XML AND is still editable
  // (never sent to SDI/FIC/manual, or scartata dallo SDI). Rows that don't
  // match are shown greyed out with a disabled checkbox — the user still sees
  // them in place.
  const isBulkEligible = (inv) => !!inv?.xml_path && !!inv?.lock_state?.is_editable;
  const eligibleOnPage = useMemo(
    () => invoices.filter(isBulkEligible).map((i) => i.id),
    [invoices]
  );
  const allPageSelected = eligibleOnPage.length > 0 && eligibleOnPage.every((id) => selected.has(id));
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        eligibleOnPage.forEach((id) => next.delete(id));
      } else {
        eligibleOnPage.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  const clearSelected = () => setSelected(new Set());

  const runManualRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await load({ withSdiRefresh: true });
    } finally {
      setRefreshing(false);
    }
  };

  const runBulkSdi = async () => {
    setBulkRunning(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/invoices/bulk/send-sdi", { invoice_ids: ids });
      const errors = (data.results || []).filter((r) => !r.ok);
      setShowBulkSdi(false);
      if (errors.length) {
        setSdiError({
          title: `Trasmissione parziale · ${data.sent}/${data.total}`,
          message: `${data.sent} fattur${data.sent === 1 ? "a" : "e"} trasmess${data.sent === 1 ? "a" : "e"} correttamente. ${errors.length} non ${errors.length === 1 ? "è andata" : "sono andate"} a buon fine.`,
          errors,
        });
      } else {
        toast.success(`Inviate ${data.sent}/${data.total} fatture al SDI`);
      }
      clearSelected();
      await load();
    } catch (e) {
      setShowBulkSdi(false);
      setSdiError({
        title: "Trasmissione al SDI non riuscita",
        message: e?.response?.data?.detail || "Non è stato possibile avviare l'invio in blocco.",
      });
    } finally {
      setBulkRunning(false);
    }
  };

  const runBulkManual = async () => {
    setBulkRunning(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/invoices/bulk/mark-manual", { invoice_ids: ids });
      const errors = (data.results || []).filter((r) => !r.ok);
      setShowBulkManual(false);
      if (errors.length) {
        setSdiError({
          title: `Trasmissione manuale · ${data.marked}/${data.total}`,
          message: `${data.marked} fattur${data.marked === 1 ? "a" : "e"} segnat${data.marked === 1 ? "a" : "e"} come trasmess${data.marked === 1 ? "a" : "e"}. ${errors.length} non ${errors.length === 1 ? "è stata" : "sono state"} elaborate.`,
          errors,
        });
      } else {
        toast.success(`Segnate ${data.marked}/${data.total} come trasmesse`);
      }
      clearSelected();
      await load();
    } catch (e) {
      setShowBulkManual(false);
      setSdiError({
        title: "Operazione non riuscita",
        message: e?.response?.data?.detail || "Non è stato possibile completare l'operazione.",
      });
    } finally {
      setBulkRunning(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="invoices-page">
      {/* Bulk confirm dialogs (rendered at page root so they don't get unmounted with the toolbar) */}
      <ConfirmDialog
        open={showBulkSdi}
        onOpenChange={setShowBulkSdi}
        onConfirm={runBulkSdi}
        loading={bulkRunning}
        icon={Send}
        tone="primary"
        title={`Invia ${selected.size} fatture allo SDI?`}
        description={
          <>
            Le fatture selezionate saranno trasmesse in blocco al Sistema di Interscambio
            dell&apos;Agenzia delle Entrate.<br />
            Una volta inviate non potranno più essere modificate salvo scarto.
          </>
        }
        confirmLabel={`Sì, invia le ${selected.size} fatture`}
        testid="bulk-sdi-confirm-dialog"
      />
      <ConfirmDialog
        open={showBulkManual}
        onOpenChange={setShowBulkManual}
        onConfirm={runBulkManual}
        loading={bulkRunning}
        icon={ClipboardCheck}
        tone="ink"
        title={`Segnare ${selected.size} fatture come trasmesse manualmente?`}
        description={
          <>
            Confermi di aver trasmesso queste fatture per conto tuo (portale Agenzia,
            commercialista, PEC)?<br />
            Verranno bloccate come immutabili ma potrai comunque riscaricare gli XML.
          </>
        }
        confirmLabel={`Sì, segna le ${selected.size} come trasmesse`}
        testid="bulk-manual-confirm-dialog"
      />
      {/* SDI error dialog (single + bulk send failures) */}
      <SdiErrorDialog
        open={!!sdiError}
        onOpenChange={(v) => { if (!v) setSdiError(null); }}
        title={sdiError?.title}
        message={sdiError?.message}
        errors={sdiError?.errors}
      />
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-2">
            <span className="w-6 h-px bg-[#1D4ED8]" />
            Archivio · {kpi.total} totali
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[0.95] text-[#0A0A0A]">
            Le tue <span className="font-serif-accent text-[#1D4ED8]">autofatture</span>.
          </h1>
        </div>
        <Link
          to="/app/upload"
          data-testid="new-invoice-btn"
          className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#1D4ED8] shadow-lg shadow-black/10 transition-all whitespace-nowrap"
        >
          <Upload className="w-4 h-4" /> Nuova autofattura
          <span className="w-5 h-5 rounded-full bg-white/15 grid place-items-center group-hover:translate-x-0.5 transition-transform">
            <ArrowRight className="w-3 h-3" />
          </span>
        </Link>
      </div>

      {/* Compact stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
        <QuickStat icon={FileText} label="Totale" value={kpi.total} accent="ink" />
        <QuickStat icon={Sparkles} label="Estratte" value={kpi.estratte} accent="blue" />
        <QuickStat icon={FileText} label="XML pronti" value={kpi.xmlReady} accent="emerald" />
        <QuickStat icon={Coins} label="Volume €" value={`€${(kpi.volumeEur).toLocaleString("it-IT", {maximumFractionDigits:0})}`} accent="coral" />
      </div>

      {/* Toolbar */}
      <div className="rounded-2xl border border-[#E8E4D9] bg-white p-3 mb-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="invoices-search"
            placeholder="Cerca fornitore, numero, file, stato SDI…"
            className="w-full h-10 pl-10 pr-3 rounded-lg border border-[#E8E4D9] bg-[#FAF9F6] focus:bg-white focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/10 outline-none text-sm transition-colors"
          />
        </div>
        <div className="relative">
          <Filter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="invoices-status-filter"
            className="h-10 pl-9 pr-8 rounded-lg border border-[#E8E4D9] bg-[#FAF9F6] text-sm font-medium focus:bg-white focus:border-[#0A0A0A] outline-none min-w-[190px] appearance-none"
          >
            {STATUS_OPTIONS.map((o, i) =>
              o.group ? (
                <optgroup key={`g-${i}`} label={o.group} />
              ) : (
                <option key={o.value} value={o.value}>{o.label}</option>
              )
            )}
          </select>
        </div>
        {(query || statusFilter !== "all") && (
          <button
            onClick={() => { setQuery(""); setStatusFilter("all"); }}
            className="h-10 px-4 rounded-lg text-sm font-medium text-[#4A4A4A] hover:bg-[#F5F2EA] transition-colors"
            data-testid="clear-filters"
          >
            Azzera filtri
          </button>
        )}
        <button
          onClick={runManualRefresh}
          disabled={refreshing}
          data-testid="invoices-refresh-btn"
          className="h-10 px-4 rounded-lg text-sm font-medium text-[#1D4ED8] hover:bg-[#F5F2EA] disabled:opacity-60 transition-colors inline-flex items-center gap-2"
          title="Aggiorna lista e stato SDI"
        >
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Aggiorna
        </button>
      </div>

      {/* Sticky bulk-action toolbar — appears when at least one invoice is selected */}
      {selected.size > 0 && (
        <div
          className="sticky top-2 z-30 mb-4 rounded-2xl border border-[#0A0A0A] bg-[#0A0A0A] text-white shadow-xl p-3 flex items-center justify-between flex-wrap gap-2 animate-in slide-in-from-top-4"
          data-testid="bulk-toolbar"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white/10 grid place-items-center">
              <ClipboardCheck className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 font-bold">Selezione multipla</div>
              <div className="text-sm font-semibold" data-testid="bulk-count">
                <span className="font-mono">{selected.size}</span> fatture selezionate
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowBulkSdi(true)}
              data-testid="bulk-send-sdi-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors"
            >
              <Send className="w-4 h-4" /> Invia allo SDI ({selected.size})
            </button>
            <button
              onClick={() => setShowBulkManual(true)}
              data-testid="bulk-mark-manual-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-white/10 border border-white/20 text-white text-sm font-semibold hover:bg-white/20 transition-colors"
            >
              <ClipboardCheck className="w-4 h-4" /> Trasmissione manuale ({selected.size})
            </button>
            <button
              onClick={clearSelected}
              data-testid="bulk-clear-btn"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-white/70 text-sm hover:text-white hover:bg-white/5 transition-colors"
            >
              <X className="w-4 h-4" /> Deseleziona
            </button>
          </div>
        </div>
      )}

      {loading && invoices.length === 0 ? (
        <div className="rounded-2xl border border-[#E8E4D9] bg-white p-16 grid place-items-center">
          <Loader2 className="w-6 h-6 text-[#737373] animate-spin" />
        </div>
      ) : total === 0 && (kpi.total || 0) === 0 ? (
        <EmptyState />
      ) : invoices.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E8E4D9] bg-white p-10 text-center">
          <div className="font-display font-bold text-lg text-[#0A0A0A]">Nessun risultato</div>
          <p className="text-sm text-[#4A4A4A] mt-1">Prova con altri filtri.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-[#E8E4D9] bg-white overflow-hidden shadow-sm" data-testid="invoices-list">
          <table className="w-full text-sm" data-testid="invoices-table">
            <caption className="sr-only">Elenco autofatture</caption>
            <thead>
              <tr className="bg-[#F5F2EA] border-b border-[#E8E4D9] text-[10px] uppercase tracking-[0.15em] text-[#737373] font-bold">
                <th scope="col" className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={toggleAllPage}
                    disabled={eligibleOnPage.length === 0}
                    data-testid="bulk-select-page"
                    aria-label="Seleziona tutte in pagina"
                    className="w-4 h-4 rounded border-[#A3A3A3] accent-[#0A0A0A] cursor-pointer disabled:opacity-30"
                  />
                </th>
                <th scope="col" className="text-left px-5 py-3 font-bold">Fornitore</th>
                <th scope="col" className="text-left px-4 py-3 font-bold hidden sm:table-cell w-32">Codice interno</th>
                <th scope="col" className="text-left px-5 py-3 font-bold hidden md:table-cell">Numero / Data</th>
                <th scope="col" className="text-left px-5 py-3 font-bold hidden lg:table-cell w-24">Doc.</th>
                <th scope="col" className="text-left px-5 py-3 font-bold hidden md:table-cell w-32">Stato</th>
                <th scope="col" className="text-left px-5 py-3 font-bold hidden lg:table-cell w-40">Stato SDI</th>
                <th scope="col" className="text-right px-5 py-3 font-bold w-36">Importo</th>
                <th scope="col" className="w-10" aria-label="Apri"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv, i) => (
                <InvoiceRow
                  key={inv.id}
                  inv={inv}
                  idx={(page - 1) * PAGE_SIZE + i}
                  isFirst={i === 0}
                  selected={selected.has(inv.id)}
                  eligible={isBulkEligible(inv)}
                  onToggle={() => toggleOne(inv.id)}
                />
              ))}
            </tbody>
          </table>
          {pages > 1 && (
            <div className="border-t border-[#E8E4D9] px-5 py-3 flex items-center justify-between text-sm bg-[#FAF9F6]" data-testid="invoices-pagination">
              <div className="text-[#737373]">
                Pagina <span className="font-semibold text-[#0A0A0A]">{page}</span> di{" "}
                <span className="font-semibold text-[#0A0A0A]">{pages}</span>
                <span className="hidden sm:inline text-[#A3A3A3] ml-2">
                  ({(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} su {total})
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  data-testid="invoices-prev-page"
                  className="inline-flex items-center gap-1 h-8 pl-2 pr-3 rounded-full border border-[#E8E4D9] bg-white text-[#0A0A0A] hover:bg-[#F5F2EA] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Precedente
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page >= pages}
                  data-testid="invoices-next-page"
                  className="inline-flex items-center gap-1 h-8 pl-3 pr-2 rounded-full border border-[#E8E4D9] bg-white text-[#0A0A0A] hover:bg-[#F5F2EA] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Successiva <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Memoized to avoid re-rendering rows when unrelated state changes (search
// input, bulk toolbar, KPI). Re-renders only when its own props change —
// crucial for large paginated lists (100 items × cheap re-render each).
const InvoiceRow = React.memo(function InvoiceRow({ inv, idx, isFirst, selected, eligible, onToggle }) {
  const ex = inv.extracted || {};
  const supplier = ex.supplier_name || inv.file_name || "—";
  const number = ex.invoice_number || "";
  const afNum = ex.autofattura_number || "";
  const date = ex.invoice_date || "";
  const amount = ex.total_amount != null
    ? `${ex.currency || "EUR"} ${Number(ex.total_amount).toLocaleString("it-IT", {minimumFractionDigits:2, maximumFractionDigits:2})}`
    : null;
  const initial = supplier.charAt(0).toUpperCase();
  const isDuplicatePending = inv.status === "duplicate_pending";

  return (
    <tr
      data-testid={`invoice-row-${idx}`}
      className={`group cursor-pointer hover:bg-[#F5F2EA]/60 transition-colors ${
        isFirst ? "" : "border-t border-[#E8E4D9]/70"
      } ${selected ? "bg-[#1D4ED8]/5" : ""}`}
      onClick={(e) => {
        if (e.target.closest('input[type="checkbox"]')) return;
        const a = e.currentTarget.querySelector("a[data-row-link]");
        if (a) a.click();
      }}
    >
      <td className="px-3 py-4 align-middle" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          disabled={!eligible}
          title={
            eligible
              ? "Seleziona per azioni bulk"
              : "Fattura già trasmessa — non selezionabile"
          }
          data-testid={`bulk-select-${idx}`}
          aria-label="Seleziona per azione multipla"
          className="w-4 h-4 rounded border-[#A3A3A3] accent-[#0A0A0A] cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
        />
      </td>
      <td className="px-5 py-4 min-w-0">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-[#F5F2EA] to-white border border-[#E8E4D9] grid place-items-center shrink-0 group-hover:from-[#1D4ED8]/10 group-hover:to-[#F97316]/5 transition-all">
            <span className="font-display font-black text-lg text-[#0A0A0A]/70 group-hover:text-[#1D4ED8] transition-colors">
              {initial}
            </span>
          </div>
          <div className="min-w-0">
            <Link
              to={`/app/invoices/${inv.id}`}
              data-row-link
              onClick={(e) => e.stopPropagation()}
              className="font-semibold text-[#0A0A0A] hover:text-[#1D4ED8] truncate block"
            >
              {supplier}
            </Link>
            <div className="flex items-center gap-1.5 mt-0.5 sm:hidden">
              {/* Su mobile il codice non ha una colonna dedicata → lo mostriamo qui */}
              <SupportCodeBadge invoiceId={inv.id} size="sm" />
              {isDuplicatePending && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded border border-amber-300 bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider"
                  title="Duplicato ricevuto via email — nessun credito consumato. Aprila per processarla."
                  data-testid={`invoice-duplicate-badge-${idx}`}
                >
                  Duplicato · in attesa
                </span>
              )}
            </div>
            <div className="md:hidden text-xs text-[#737373] font-mono truncate mt-0.5">
              {afNum ? (
                <span className="text-[#1D4ED8] font-bold">{afNum}</span>
              ) : number ? `n. ${number}` : date ? date : (
                <span className="italic text-[#A3A3A3]">Dati in estrazione…</span>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-4 hidden sm:table-cell" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-col items-start gap-1">
          <SupportCodeBadge invoiceId={inv.id} size="md" data-testid={`invoice-support-code-${idx}`} />
          {isDuplicatePending && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded border border-amber-300 bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider"
              title="Duplicato ricevuto via email — nessun credito consumato. Aprila per processarla."
              data-testid={`invoice-duplicate-badge-${idx}`}
            >
              Duplicato · in attesa
            </span>
          )}
        </div>
      </td>
      <td className="px-5 py-4 hidden md:table-cell text-xs text-[#737373] font-mono">
        {afNum && (
          <div className="text-[#1D4ED8] font-bold text-xs mb-0.5 truncate max-w-[220px]" data-testid={`invoice-af-num-${idx}`}>
            {afNum}
          </div>
        )}
        {number && <div className="truncate max-w-[220px]">n. {number}</div>}
        {date && (
          <div className="inline-flex items-center gap-1 mt-0.5">
            <Calendar className="w-3 h-3" />
            {date}
          </div>
        )}
        {!afNum && !number && !date && <span className="italic text-[#A3A3A3]">Dati in estrazione…</span>}
      </td>
      <td className="px-5 py-4 hidden lg:table-cell">
        {inv.document_type && (
          <span className="text-[10px] font-mono font-bold text-[#1D4ED8] bg-[#1D4ED8]/10 px-2 py-0.5 rounded-full whitespace-nowrap">
            {inv.document_type}
          </span>
        )}
      </td>
      <td className="px-5 py-4 hidden md:table-cell">
        <StatusBadge status={inv.status} />
      </td>
      <td className="px-5 py-4 hidden lg:table-cell">
        <SdiTxBadge inv={inv} />
      </td>
      <td className="px-5 py-4 text-right font-mono font-semibold text-[#0A0A0A] text-sm tabular-nums whitespace-nowrap">
        {amount || <span className="text-[#A3A3A3]">—</span>}
      </td>
      <td className="px-5 py-4 text-right">
        <ArrowUpRight className="inline w-4 h-4 text-[#737373] group-hover:text-[#0A0A0A] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
      </td>
    </tr>
  );
});

function QuickStat({ icon: Icon, label, value, accent }) {
  const bg = {
    ink: "text-[#0A0A0A]",
    blue: "text-[#1D4ED8]",
    emerald: "text-[#059669]",
    coral: "text-[#F97316]",
  }[accent];
  return (
    <div className="rounded-xl border border-[#E8E4D9] bg-white px-4 py-3 flex items-center gap-3">
      <Icon className={`w-4 h-4 ${bg}`} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-[#737373] font-semibold truncate">{label}</div>
        <div className="font-display font-extrabold text-lg text-[#0A0A0A] tracking-tight truncate">{value}</div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="relative rounded-3xl border border-[#E8E4D9] bg-gradient-to-br from-white to-[#F5F2EA] overflow-hidden">
      <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#1D4ED8]/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-[#F97316]/10 rounded-full blur-3xl" />
      <div className="relative p-10 md:p-16 max-w-2xl mx-auto text-center">
        <div className="w-16 h-16 rounded-2xl bg-white border border-[#E8E4D9] grid place-items-center mx-auto mb-6 shadow-sm">
          <FileText className="w-7 h-7 text-[#1D4ED8]" />
        </div>
        <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tighter text-[#0A0A0A]">
          Ancora nessuna <span className="font-serif-accent text-[#F97316]">fattura</span>.
        </h2>
        <p className="mt-3 text-[#4A4A4A] max-w-md mx-auto">
          Carica il tuo primo PDF estero (Google, Meta, Amazon…) e in 12 secondi hai l&apos;XML pronto per lo SDI.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/app/upload"
            data-testid="empty-upload-cta"
            className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#1D4ED8] shadow-lg shadow-black/10 transition-all"
          >
            <Upload className="w-4 h-4" /> Carica il primo PDF
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
          <Link
            to="/app"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#E8E4D9] bg-white text-[#0A0A0A] text-sm font-semibold hover:bg-[#F5F2EA] transition-colors"
          >
            Torna alla dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
